import React from 'react';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Collapse, Paper, Stack, Tooltip, Typography } from '@mui/material';

import { isRowApproved, isRowManuallyApproved } from '../../manualOverridePersistence';
import ChecklistEntityCardHeader, { type EntityCardHeaderCell } from '../../reportCards/ChecklistEntityCardHeader';
import ManualChip from '../../reportCards/ManualChip';
import { formatPrice } from '../../shared/format';
import type { ManualOverrideState } from '../../types';

import InclusionRow, { INCLUSION_ROW_GRID_TEMPLATE_COLUMNS } from './InclusionRow';
import InclusionRowHeader from './InclusionRowHeader';
import { getDealOptionsInclusionsSearchText } from './dealOptionsInclusionsQueue';
import type { DealOptionRowView } from './types';

interface Props {
  deal: DealOptionRowView;
  expandedRows: Record<string, boolean>;
  manualOverrides: ManualOverrideState;
  searchQuery: string;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onToggleDetails: (rowId: string, defaultOpen?: boolean) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function DealCard({
  deal,
  expandedRows,
  manualOverrides,
  searchQuery,
  showAutoApproved,
  onApprovalChange,
  onToggleDetails,
  onUnapprovalChange,
}: Props) {
  const isDealApproved = isRowApproved(deal, manualOverrides);
  const isDealManuallyApproved = isRowManuallyApproved(deal, manualOverrides);
  // The deal option itself counts as one more approvable item, alongside its inclusions.
  const totalItemCount = deal.inclusions.length + 1;
  const pendingCount =
    deal.inclusions.filter((inc) => !isRowApproved(inc, manualOverrides)).length + (isDealApproved ? 0 : 1);
  const isOpen = expandedRows[deal.id] ?? true;
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const visibleInclusions = deal.inclusions.filter((inc) => {
    if (!showAutoApproved && isRowApproved(inc, manualOverrides)) {
      return false;
    }
    return (
      !normalizedSearchQuery || getDealOptionsInclusionsSearchText(inc).toLowerCase().includes(normalizedSearchQuery)
    );
  });
  const hasSearchMiss = !!normalizedSearchQuery && deal.inclusions.length > 0 && visibleInclusions.length === 0;

  const cells: Array<EntityCardHeaderCell> = [
    { label: 'Sell price', value: formatPrice(deal.sellPrice, deal.currency) },
    { label: 'Cost price', value: formatPrice(deal.costPrice, deal.currency) },
    { label: '% margin', value: deal.marginPercent != null ? `${deal.marginPercent}%` : '—' },
    { label: 'Rack rate', value: formatPrice(deal.rackRate, deal.currency) },
    {
      label: 'Room asset',
      content: (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
          <Box
            component="span"
            sx={{
              bgcolor: deal.roomAssetLinked ? 'success.main' : 'warning.main',
              borderRadius: '50%',
              flexShrink: 0,
              height: 8,
              width: 8,
            }}
          />
          <Typography variant="body2" fontWeight={600}>
            {deal.roomAssetLinked ? 'Linked' : 'Unlinked'}
          </Typography>
        </Stack>
      ),
    },
  ];

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1.25, overflow: 'hidden' }}>
      <ChecklistEntityCardHeader
        approved={isDealApproved}
        cells={cells}
        countLabel={`${deal.inclusions.length} inclusion${deal.inclusions.length === 1 ? '' : 's'}`}
        expanded={isOpen}
        gridTemplateColumns={INCLUSION_ROW_GRID_TEMPLATE_COLUMNS}
        statusApproved={pendingCount === 0}
        statusCount={pendingCount > 0 ? pendingCount : totalItemCount}
        title={deal.dealOptionName || '—'}
        titleAdornment={
          <>
            {deal.needsFlexiNightsReview && (
              <Tooltip title="Set flexi nights to TRUE on the package in Admin Portal">
                <WarningAmberIcon color="error" fontSize="small" onClick={(event) => event.stopPropagation()} />
              </Tooltip>
            )}
            {isDealManuallyApproved && <ManualChip />}
          </>
        }
        onApprove={() => onApprovalChange(deal.id)}
        onToggle={() => onToggleDetails(deal.id, true)}
        onUnapprove={() => onUnapprovalChange(deal.id)}
      />

      <Collapse in={isOpen}>
        {visibleInclusions.length === 0 ? (
          <Box sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {hasSearchMiss
                ? 'No inclusions match your search.'
                : deal.inclusions.length === 0
                  ? 'No inclusions configured.'
                  : 'All inclusions approved.'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
            <InclusionRowHeader />
            {visibleInclusions.map((inc) => (
              <InclusionRow
                key={inc.id}
                currency={deal.currency}
                inc={inc}
                approved={isRowApproved(inc, manualOverrides)}
                manuallyApproved={isRowManuallyApproved(inc, manualOverrides)}
                onApprovalChange={onApprovalChange}
                onUnapprovalChange={onUnapprovalChange}
              />
            ))}
          </Box>
        )}
      </Collapse>
    </Paper>
  );
}
